import { useState, useEffect, useCallback  } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Header from '../Header';
import Sidebar from '../Sidebar';
import { useClientData } from '../hooks/useClientData';
import axios from 'axios';
import EditUnderwriterModal from '../components/EditUnderwriterModal';
import { usePermissions } from '../context/PermissionsContext';

const API_URL = process.env.REACT_APP_API_URL;

type FileItem = {
  file_id: number;
  document_id?: number;
  underwriter_id: number;
  file_type: string;
  description: string;
  file_path: string;
  created_at: string;
  uploaded_at?: string;
  file_name?: string;
  file_size?: number;
  mime_type?: string;
};

type Note = {
  note_id: number;
  underwriter_id: number;
  note_text: string;
  createdAt: string;
};

type UnderwriterContact = {
  contact_id?: number;
  underwriter_id: number;
  phone_number: string;
  email_address: string;
  email_type: string;
  created_at?: string;
  updated_at?: string;
};

interface UnderwriterData {
  id: number;
  status: 'Active' | 'Inactive';
  underwriter_name: string;
  consultant_reference_number: string;
  tax_number: string;
  consultant_name: string;
  consultant_surname: string;
  consultant_phone_number: string;
  consultant_email: string;
  quotations_email: string;
  claims_email: string;
  instructions_email: string;
  created_at: string;
  updated_at: string;
  addressLine1?: string;
  addressLine2?: string;
  city?: string;
  region?: string;
  suburb?: string;
  postalCode?: string;
  country?: string;
  emailType?: string;
  underwriter_phone_number?: string;
  underwriter_email_address?: string;
  underwriter_email_type?: string;
  notes?: Note[];
  files?: FileItem[];
  contacts?: UnderwriterContact[];
}

const UnderwriterDetails = () => {
  const { can } = usePermissions();
  const [isSidebarVisible, setIsSidebarVisible] = useState(true);
  const [activeTab, setActiveTab] = useState('profile');
  const [underwriter, setUnderwriter] = useState<UnderwriterData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { id: clientId, underwriterId } = useParams();
  const { user, client, isLoading: clientLoading } = useClientData(clientId);
  const navigate = useNavigate();
  const toggleSidebar = () => setIsSidebarVisible(!isSidebarVisible);
  const [newNote, setNewNote] = useState("");


  // Address form state
  const [addressForm, setAddressForm] = useState({
    addressLine1: "",
    addressLine2: "",
    city: "",
    region: "",
    suburb: "",
    postalCode: "",
    country: "",
  });
  const [isEditingAddress, setIsEditingAddress] = useState(false);

  // Contact form state
  const [contactForm, setContactForm] = useState({
    phone_number: "",
    email_address: "",
    email_type: "",
  });
  const [contacts, setContacts] = useState<UnderwriterContact[]>([]);

  const [isAddingContact, setIsAddingContact] = useState(false);
  const [isAddingNote, setIsAddingNote] = useState(false);
  const [noteText, setNoteText] = useState("");
  const [notes, setNotes] = useState<Note[]>([]);
  const [isUploadingFile, setIsUploadingFile] = useState(false);
  const [fileType, setFileType] = useState("");
  const [description, setDescription] = useState("");
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);

  const getAuthHeaders = () => {
    const token = localStorage.getItem('token');
    return token ? { headers: { Authorization: `Bearer ${token}` } } : {};
  };
  

  const fetchNotes = useCallback(async () => {
    if (!underwriterId) return;
    try {
      const notesResponse = await axios.get<Note[] | { data: Note[]; success: boolean; }>(
        `${API_URL}/api/underwriters/${underwriterId}/notes`,
        getAuthHeaders()
      );
      console.log("✅ Notes response:", notesResponse.data);
      
      let notesData: Note[] = [];
      const responseData = notesResponse.data;
      
      if (Array.isArray(responseData)) {
        notesData = responseData;
      } else if (responseData && typeof responseData === 'object') {
        if ('data' in responseData && Array.isArray(responseData.data)) {
          notesData = responseData.data;
        } else if ('notes' in responseData && Array.isArray(responseData.notes)) {
          notesData = responseData.notes;
        } else if ('results' in responseData && Array.isArray(responseData.results)) {
          notesData = responseData.results;
        }
      }
      
      console.log("📋 Processed notes data:", notesData);
      const sortedNotes = notesData.sort(
        (a: Note, b: Note) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
      );
      setNotes(sortedNotes);
    
    } catch (notesError) {
      console.error("❌ Error fetching notes:", notesError);
      if (axios.isAxiosError(notesError)) { // CHANGE THIS LINE
        console.error("Notes error details:", {
          status: notesError.response?.status,
          data: notesError.response?.data
        });
      }
      setNotes([]);
    }
  }, [underwriterId, API_URL]);

  useEffect(() => {
  const token = localStorage.getItem('token');
  console.log('🔐 Token check in UnderwriterDetails:', token ? 'Present' : 'Missing');
  
  if (!token) {
    console.error('❌ No token found! Redirecting to login...');
    navigate('/login');
  }
}, [navigate]);

  useEffect(() => {
    if (underwriterId) fetchNotes();
  }, [underwriterId]);

    const fetchUnderwriterDetails = useCallback(async () => {
  if (!underwriterId) {
    setError('No underwriter ID provided');
    setIsLoading(false);
    return;
  }

  setIsLoading(true);
  setError(null);

  try {
    // Fetch underwriter basic data
    const response = await axios.get(
      `${API_URL}/api/underwriters/${underwriterId}`,
      getAuthHeaders()
    );
    const underwriterData = response.data.data || response.data;

    if (!underwriterData) throw new Error('No data received from server');

    // Fetch files
    try {
      const filesResponse = await axios.get(
        `${API_URL}/api/underwriters/${underwriterId}/files`,
        getAuthHeaders()
      );
      const filesData = filesResponse.data.data || filesResponse.data || filesResponse.data.files || [];
      underwriterData.files = filesData;
    } catch (fileError) {
      console.error("❌ Error fetching files:", fileError);
      underwriterData.files = [];
    }

    // Fetch notes
    await fetchNotes();

    // Fetch contacts
    try {
      const contactsResponse = await axios.get(
        `${API_URL}/api/underwriters/${underwriterId}/contacts`,
        getAuthHeaders()
      );
      let contactsData: UnderwriterContact[] = [];
      
      // Process the response data
      const responseData = contactsResponse.data;
      if (Array.isArray(responseData)) {
        contactsData = responseData;
      } else if (responseData && typeof responseData === 'object') {
        if ('data' in responseData && Array.isArray(responseData.data)) {
          contactsData = responseData.data;
        } else if ('contacts' in responseData && Array.isArray(responseData.contacts)) {
          contactsData = responseData.contacts;
        } else if ('results' in responseData && Array.isArray(responseData.results)) {
          contactsData = responseData.results;
        }
      }
      
      setContacts(contactsData);
    } catch (contactsError) {
      console.error("❌ Error fetching contacts:", contactsError);
      setContacts([]);
    }

    // Fetch addresses
    try {
      const addressesResponse = await axios.get(
        `${API_URL}/api/underwriters/${underwriterId}/addresses`,
        getAuthHeaders()
      );
      let addressesData: any[] = [];

      const responseData = addressesResponse.data;
      if (Array.isArray(responseData)) {
        addressesData = responseData;
      } else if (responseData && typeof responseData === 'object') {
        if ('data' in responseData && Array.isArray(responseData.data)) {
          addressesData = responseData.data;
        } else if ('addresses' in responseData && Array.isArray(responseData.addresses)) {
          addressesData = responseData.addresses;
        } else if ('results' in responseData && Array.isArray(responseData.results)) {
          addressesData = responseData.results;
        }
      }

      if (addressesData.length > 0) {
        const primaryAddress = addressesData[0];
        setUnderwriter(prev => {
          if (!prev) return prev;
          return {
            ...prev,
            addressLine1: primaryAddress.address_line1 || "",
            addressLine2: primaryAddress.address_line2 || "",
            city: primaryAddress.city_town || "",
            region: primaryAddress.region || "",
            suburb: primaryAddress.suburb || "",
            postalCode: primaryAddress.postal_code || "",
            country: primaryAddress.country || "",
          };
        });
      }
    } catch (addressesError) {
      console.error("❌ Error fetching addresses:", addressesError);
    }

    // Set basic underwriter info
    setUnderwriter(underwriterData as UnderwriterData);

    // Initialize forms
    setAddressForm({
      addressLine1: underwriterData.addressLine1 || "",
      addressLine2: underwriterData.addressLine2 || "",
      city: underwriterData.city || "",
      region: underwriterData.region || "",
      suburb: underwriterData.suburb || "",
      postalCode: underwriterData.postalCode || "",
      country: underwriterData.country || "",
    });

    setContactForm({
      phone_number: underwriterData.underwriter_phone_number || "",
      email_address: underwriterData.underwriter_email_address || "",
      email_type: underwriterData.underwriter_email_type || "",
    });

  } catch (err) {
    if (axios.isAxiosError(err)) {
      console.error('❌ Axios error:', {
        message: err.message,
        status: err.response?.status,
        url: err.config?.url,
        data: err.response?.data
      });
      setError(err.response?.status === 404
        ? `Underwriter with ID ${underwriterId} not found.`
        : 'Server error. Please try again later.'
      );
    } else {
      console.error('Unexpected error:', err);
      setError('An unexpected error occurred.');
    }
  } finally {
    setIsLoading(false);
  }
}, [underwriterId, fetchNotes, API_URL]);


  useEffect(() => {
  fetchUnderwriterDetails();
}, [underwriterId]);

  // Address change handler
  const handleAddressChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setAddressForm((prev) => ({ ...prev, [name]: value }));
  };

  // Save address handler
  const handleSaveAddress = async () => {
    try {
      console.log("📤 Saving address with data:", addressForm);
      await axios.post(
        `${API_URL}/api/underwriters/${underwriterId}/addresses`,
        {
          underwriterId: parseInt(underwriterId || '0'),
          address_line1: addressForm.addressLine1,
          address_line2: addressForm.addressLine2,
          city_town: addressForm.city,
          region: addressForm.region,
          suburb: addressForm.suburb,
          postal_code: addressForm.postalCode,
          country: addressForm.country,
        },
        getAuthHeaders()
      );

      setUnderwriter((prev) =>
        prev ? { ...prev, ...addressForm } : prev
      );
      setIsEditingAddress(false);
     } catch (err) {
        console.error("❌ Error saving address:", err);
        if (axios.isAxiosError(err)) {
          console.error("Address error details:", {
            status: err.response?.status,
            data: err.response?.data,
            url: err.config?.url
          });
        }
        alert("Failed to save address. Check console for details.");
      }
  };

  // Cancel address edit handler
  const handleCancelAddress = () => {
    if (!underwriter) return;
    setAddressForm({
      addressLine1: underwriter.addressLine1 || "",
      addressLine2: underwriter.addressLine2 || "",
      city: underwriter.city || "",
      region: underwriter.region || "",
      suburb: underwriter.suburb || "",
      postalCode: underwriter.postalCode || "",
      country: underwriter.country || "",
    });
    setIsEditingAddress(false);
  };

  // Start fresh form for adding new address handler
  const handleAddNewAddress = () => {
    setAddressForm({
      addressLine1: "",
      addressLine2: "",
      city: "",
      region: "",
      suburb: "",
      postalCode: "",
      country: "",
    });
    setIsEditingAddress(true);
  };

  // Contact change handler
  const handleContactChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setContactForm((prev) => ({ ...prev, [name]: value }));
  };

  // Save contact handler
  const handleSaveContact = async () => {
    try {
      console.log("Saving contact with data:", contactForm);

      const response = await axios.post(
        `${API_URL}/api/underwriters/${underwriterId}/contacts`,
        {
          ...contactForm,
          underwriter_id: underwriterId
        },
        getAuthHeaders()
      );

      console.log("Contact save response:", response.data);
      
      const newContact = response.data.data || response.data;
      
      if (newContact) {
        setContacts((prev) => [...prev, newContact]);
        // Reset the form
        setContactForm({
          phone_number: "",
          email_address: "",
          email_type: "",
        });
        setIsAddingContact(false);
        alert("Contact saved successfully!");
      } else {
        throw new Error("Invalid contact response from server");
      }
    } catch (err) {
      console.error("Error saving contact:", err);
      if (axios.isAxiosError(err)) {
        console.error("Axios error details:", {
          status: err.response?.status,
          data: err.response?.data,
          url: err.config?.url
        });
      }
      alert("Failed to save contact. Check console for details.");
    }
  };

  // Save a new note
  const handleSaveNote = async () => {
    if (!noteText.trim()) return; // avoid empty notes
    try {
      // Make POST request to backend
      const response = await axios.post(
        `${API_URL}/api/underwriters/${underwriterId}/notes`,
        { note_text: noteText },
        getAuthHeaders()
      );

      if (response.data.success) {
        // Add new note to local state
        setNotes((prev) => [...prev, response.data.data]);
        setNoteText(""); // clear input
        setError(null);
      } else {
        setError("Failed to save note");
      }
    } catch (err: any) {
      console.error("Error saving note:", err);
      setError(err.response?.data?.message || err.message || "Unknown error");
    }
  };




  // File Upload Handler
  const handleFileUpload = async () => {
    if (!selectedFile || !fileType || !description) {
      alert("Please fill all fields and select a file before uploading.");
      return;
    }

    try {
      const formData = new FormData();
      formData.append("file", selectedFile);
      formData.append("fileType", fileType);
      formData.append("description", description);

      await axios.post(
      `${API_URL}/api/underwriters/${underwriterId}/files`,
      formData,
      {
        ...getAuthHeaders(), // ADD THIS
        headers: {
          ...getAuthHeaders().headers,
          "Content-Type": "multipart/form-data",
        },
      }
    );
      

      setFileType("");
      setDescription("");
      setSelectedFile(null);
      setIsUploadingFile(false);

      const filesResponse = await axios.get(
      `${API_URL}/api/underwriters/${underwriterId}/files`,
      getAuthHeaders() // ADD THIS
    );
      setUnderwriter((prev) => prev ? { ...prev, files: filesResponse.data } : prev);

    } catch (err) {
      console.error("Error uploading file:", err);
      alert("Failed to upload file.");
    }
  };

  if (isLoading || clientLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p>Loading underwriter details...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center text-red-600">
        <p>Error: {error}</p>
        <button onClick={() => navigate('/underwriters')} className="ml-4 text-blue-600 hover:underline">
          Back to Underwriters
        </button>
      </div>
    );
  }

  if (!underwriter) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p>Underwriter not found.</p>
        <button onClick={() => navigate('/underwriters')} className="ml-4 text-blue-600 hover:underline">
          Back to Underwriters
        </button>
      </div>
    );
  }

  if (!user) return <div>Loading...</div>;

  return (
    <div className="min-h-screen bg-gray-100 flex relative">
      <Sidebar isVisible={isSidebarVisible} toggleSidebar={toggleSidebar} />
      
      <div className={`flex-1 flex flex-col transition-all duration-300 ${isSidebarVisible ? 'ml-64' : 'ml-0'}`}>
        <Header
          title="Underwriter Details"
          user={user}
          client={null}
          toggleSidebar={toggleSidebar}
          isSidebarVisible={isSidebarVisible}
          showClientNumber={false}
        />
        
        <div className="flex-1 p-6">
          <div className="bg-white rounded-xl shadow-lg p-6">
            {/* Back button */}
            <div className="mb-6">
              <button
                onClick={() => navigate('/underwriters')}
                className="inline-flex items-center text-blue-600 hover:text-blue-800 font-medium"
              >
                <svg
                  className="w-5 h-5 mr-2"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M15 19l-7-7 7-7"
                  />
                </svg>
                Back to Underwriters
              </button>
            </div>

            {/* Big Underwriter Name */}
            {underwriter && (
              <h1 className="text-3xl font-bold text-gray-900 mb-6">
                {underwriter.underwriter_name}
              </h1>
            )}

            {/* Edit Underwriter Button*/}
            {can('insurers', 'write') && (
              <div className="flex justify-end mb-6">
                <button
                  onClick={() => setIsEditModalOpen(true)}
                  className="bg-blue-500 hover:bg-blue-600 text-white py-2 px-4 rounded-lg"
                >
                  Edit Underwriter
                </button>
              </div>
            )}

            {/* Tabs */}
            <div className="border-b border-gray-200 mb-6">
              <nav className="-mb-px flex space-x-8">
                {['profile', 'address', 'contacts', 'notes', 'files'].map((tab) => (
                  <button
                    key={tab}
                    onClick={() => setActiveTab(tab)}
                    className={`py-4 px-1 border-b-2 font-medium text-sm ${
                      activeTab === tab
                        ? 'border-blue-500 text-blue-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }`}
                  >
                    {tab.charAt(0).toUpperCase() + tab.slice(1)}
                  </button>
                ))}
              </nav>
            </div>

            {/* Tab Content */}
            <div className="space-y-4">
              {activeTab === 'profile' && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <h3 className="text-lg font-semibold mb-4">Underwriter Information</h3>
                    <div className="space-y-4">
                      <div key="name">
                        <label className="text-sm font-medium text-gray-500 block mb-1">Name</label>
                        <p className="text-gray-900">{underwriter.underwriter_name}</p>
                      </div>

                      <div key="status">
                        <label className="text-sm font-medium text-gray-500 block mb-1">Status</label>
                        <p className="text-gray-900 font-medium">
                          {underwriter.status}
                        </p>
                      </div>

                      <div key="reference">
                        <label className="text-sm font-medium text-gray-500 block mb-1">Broker Code</label>
                        <p className="text-gray-900">{underwriter.consultant_reference_number}</p>
                      </div>
                      <div key="tax">
                        <label className="text-sm font-medium text-gray-500 block mb-1">Tax Number</label>
                        <p className="text-gray-900">{underwriter.tax_number || 'Not provided'}</p>
                      </div>
                      <div key="fullname">
                        <label className="text-sm font-medium text-gray-500 block mb-1">Consultant Full Name</label>
                        <p className="text-gray-900">{underwriter.consultant_name} {underwriter.consultant_surname}</p>
                      </div>
                    </div>
                  </div>
                  
                  <div>
                    <h3 className="text-lg font-semibold mb-4">Contact Information</h3>
                    <div className="space-y-4">
                      <div key="consultantEmail">
                        <label className="text-sm font-medium text-gray-500 block mb-1">Consultant Email</label>
                        <p className="text-blue-600">
                          <a href={`mailto:${underwriter.consultant_email}`}>{underwriter.consultant_email}</a>
                        </p>
                      </div>

                      <div key="consultantPhone">
                        <label className="text-sm font-medium text-gray-500 block mb-1">Consultant Phone Number</label>
                        <p className="text-gray-900">{underwriter.consultant_phone_number}</p>
                      </div>

                      <div key="quotationsEmail">
                        <label className="text-sm font-medium text-gray-500 block mb-1">Quotations Email</label>
                        <p className="text-blue-600">
                          <a href={`mailto:${underwriter.quotations_email}`}>
                            {underwriter.quotations_email}
                          </a>
                        </p>
                      </div>

                      <div key="claimsEmail">
                        <label className="text-sm font-medium text-gray-500 block mb-1">Claims Email</label>
                        <p className="text-blue-600">
                          <a href={`mailto:${underwriter.claims_email}`}>
                            {underwriter.claims_email}
                          </a>
                        </p>
                      </div>

                      <div key="instructionsEmail">
                        <label className="text-sm font-medium text-gray-500 block mb-1">Instructions Email</label>
                        <p className="text-blue-600">
                          <a href={`mailto:${underwriter.instructions_email}`}>
                            {underwriter.instructions_email}
                          </a>
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Address Tab */}
              {activeTab === 'address' && (
                <div>
                  <h3 className="text-lg font-semibold mb-4">Address Information</h3>

                  {!isEditingAddress ? (
                    <div className="bg-gray-50 p-6 rounded-xl shadow-sm border border-gray-200">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div key="address1">
                          <p className="text-sm text-gray-500">Address Line 1</p>
                          <p className="text-lg font-medium text-gray-900">{underwriter.addressLine1 || "Not provided"}</p>
                        </div>
                        <div key="address2">
                          <p className="text-sm text-gray-500">Address Line 2</p>
                          <p className="text-lg font-medium text-gray-900">{underwriter.addressLine2 || "Not provided"}</p>
                        </div>
                        <div key="city">
                          <p className="text-sm text-gray-500">City / Town</p>
                          <p className="text-lg font-medium text-gray-900">{underwriter.city || "Not provided"}</p>
                        </div>
                        <div key="region">
                          <p className="text-sm text-gray-500">Region</p>
                          <p className="text-lg font-medium text-gray-900">{underwriter.region || "Not provided"}</p>
                        </div>
                        <div key="suburb">
                          <p className="text-sm text-gray-500">Suburb</p>
                          <p className="text-lg font-medium text-gray-900">{underwriter.suburb || "Not provided"}</p>
                        </div>
                        <div key="postalCode">
                          <p className="text-sm text-gray-500">Postal Code</p>
                          <p className="text-lg font-medium text-gray-900">{underwriter.postalCode || "Not provided"}</p>
                        </div>
                        <div key="country">
                          <p className="text-sm text-gray-500">Country</p>
                          <p className="text-lg font-medium text-gray-900">{underwriter.country || "Not provided"}</p>
                        </div>
                      </div>

                      {can('insurers', 'write') && (
                      <div className="flex space-x-3 mt-6">
                        {underwriter.addressLine1 ? (
                          <button
                            onClick={() => setIsEditingAddress(true)}
                            className="bg-blue-500 hover:bg-blue-600 text-white px-5 py-2 rounded-lg shadow"
                          >
                            Edit Address
                          </button>
                        ) : (
                          <button
                            onClick={handleAddNewAddress}
                            className="bg-green-500 hover:bg-green-600 text-white px-5 py-2 rounded-lg shadow"
                          >
                            Add New Address
                          </button>
                        )}
                      </div>
                    )}
                    </div>
                    
                  ) : (
                    <form className="space-y-4">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700">Address Line 1</label>
                          <input
                            type="text"
                            name="addressLine1"
                            value={addressForm.addressLine1}
                            onChange={handleAddressChange}
                            className="mt-1 w-full border rounded-lg px-3 py-2"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700">Address Line 2</label>
                          <input
                            type="text"
                            name="addressLine2"
                            value={addressForm.addressLine2}
                            onChange={handleAddressChange}
                            className="mt-1 w-full border rounded-lg px-3 py-2"
                          />
                        </div>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700">City/Town</label>
                          <input
                            type="text"
                            name="city"
                            value={addressForm.city}
                            onChange={handleAddressChange}
                            className="mt-1 w-full border rounded-lg px-3 py-2"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700">Region</label>
                          <input
                            type="text"
                            name="region"
                            value={addressForm.region}
                            onChange={handleAddressChange}
                            className="mt-1 w-full border rounded-lg px-3 py-2"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700">Suburb</label>
                          <input
                            type="text"
                            name="suburb"
                            value={addressForm.suburb}
                            onChange={handleAddressChange}
                            className="mt-1 w-full border rounded-lg px-3 py-2"
                          />
                        </div>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700">Postal Code</label>
                          <input
                            type="text"
                            name="postalCode"
                            value={addressForm.postalCode}
                            onChange={handleAddressChange}
                            className="mt-1 w-full border rounded-lg px-3 py-2"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700">Country</label>
                          <input
                            type="text"
                            name="country"
                            value={addressForm.country}
                            onChange={handleAddressChange}
                            className="mt-1 w-full border rounded-lg px-3 py-2"
                          />
                        </div>
                      </div>

                      <div className="flex justify-end space-x-3">
                        <button
                          type="button"
                          onClick={handleCancelAddress}
                          className="bg-gray-300 hover:bg-gray-400 text-gray-800 px-6 py-2 rounded-lg"
                        >
                          Cancel
                        </button>
                        <button
                          type="button"
                          onClick={handleSaveAddress}
                          className="bg-blue-500 hover:bg-blue-600 text-white px-6 py-2 rounded-lg"
                        >
                          Save
                        </button>
                      </div>
                    </form>
                  )}
                </div>
              )}

              {/* Contacts Tab */}
              {activeTab === "contacts" && (
                <div className="p-6 space-y-4">
                  <h2 className="text-xl font-semibold">Contact Information</h2>

                  {/* Display legacy contact info if it exists */}
                  {underwriter && (underwriter.underwriter_phone_number || underwriter.underwriter_email_address) && (
                    <div className="bg-yellow-50 p-4 rounded-lg border border-yellow-200 mb-4">
                      <h3 className="text-lg font-semibold text-yellow-800 mb-2">Legacy Contact Information</h3>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        {underwriter.underwriter_phone_number && (
                          <p><strong>Phone:</strong> {underwriter.underwriter_phone_number}</p>
                        )}
                        {underwriter.underwriter_email_address && (
                          <p><strong>Email:</strong> {underwriter.underwriter_email_address}</p>
                        )}
                        {underwriter.underwriter_email_type && (
                          <p><strong>Type:</strong> {underwriter.underwriter_email_type}</p>
                        )}
                      </div>
                      <p className="text-sm text-yellow-600 mt-2">
                        This is legacy data. Please add contacts using the new system below.
                      </p>
                    </div>
                  )}

                  {/* Display multiple contacts - FIELDS NEXT TO EACH OTHER */}
                  {contacts.length > 0 ? (
                    <div className="space-y-4">
                      {contacts.map((contact) => (
                        <div key={contact.contact_id} className="bg-gray-50 p-4 rounded-lg shadow border border-gray-200">
                          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            <div>
                              <strong>Phone:</strong> {contact.phone_number || "—"}
                            </div>
                            <div>
                              <strong>Email:</strong> {contact.email_address || "—"}
                            </div>
                            <div>
                              <strong>Type:</strong> {contact.email_type || "—"}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-gray-500">No contacts available. Add your first contact below.</p>
                  )}

                  {/* Add New Contact Button */}
                  {can('insurers', 'write') && !isAddingContact && (
                    <button
                      onClick={() => {
                        setContactForm({
                          phone_number: "",
                          email_address: "",
                          email_type: "",
                        });
                        setIsAddingContact(true);
                      }}
                      className="bg-green-500 hover:bg-green-600 text-white px-5 py-2 rounded-lg"
                    >
                      Add New Contact
                    </button>
                  )}

                  {/* Contact Form - FIELDS NEXT TO EACH OTHER */}
                  {isAddingContact && (
                    <div className="space-y-3 bg-gray-50 p-4 rounded-lg shadow">
                      <h3 className="text-lg font-semibold">Add New Contact</h3>
                      
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Phone Number
                          </label>
                          <input
                            type="text"
                            name="phone_number"
                            value={contactForm.phone_number}
                            onChange={handleContactChange}
                            className="w-full border rounded-lg px-3 py-2"
                            placeholder="+1234567890"
                          />
                        </div>
                        
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Email Address
                          </label>
                          <input
                            type="email"
                            name="email_address"
                            value={contactForm.email_address}
                            onChange={handleContactChange}
                            className="w-full border rounded-lg px-3 py-2"
                            placeholder="contact@example.com"
                          />
                        </div>
                        
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Email Type
                          </label>
                          <select
                            name="email_type"
                            value={contactForm.email_type}
                            onChange={handleContactChange}
                            className="w-full border rounded-lg px-3 py-2"
                          >
                            <option value="">Select Email Type</option>
                            <option value="Personal">Personal</option>
                            <option value="Business">Business</option>
                          </select>
                        </div>
                      </div>

                      <div className="flex gap-3 mt-4">
                        <button
                          onClick={handleSaveContact}
                          className="bg-blue-500 hover:bg-blue-600 text-white px-5 py-2 rounded-lg"
                        >
                          Save Contact
                        </button>
                        <button
                          onClick={() => setIsAddingContact(false)}
                          className="bg-gray-400 hover:bg-gray-500 text-white px-5 py-2 rounded-lg"
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Notes Tab */}
              {activeTab === "notes" && (
                <div className="p-6 space-y-4">
                  <h3 className="text-lg font-semibold mb-4">Notes</h3>

                  {can('insurers', 'write') && !isAddingNote && (
                    <button
                      onClick={() => setIsAddingNote(true)}
                      className="bg-green-500 hover:bg-green-600 text-white px-5 py-2 rounded-lg"
                    >
                      Add Note
                    </button>
                  )}
                  
                  {isAddingNote && (
                    <div className="space-y-3 bg-gray-50 p-4 rounded-lg shadow mb-4">
                      <textarea
                        placeholder="Enter your note..."
                        value={noteText}
                        onChange={(e) => setNoteText(e.target.value)}
                        className="w-full border rounded-lg px-3 py-2"
                        rows={4}
                      />
                      <div className="flex gap-3">
                        <button
                          onClick={handleSaveNote}
                          className="bg-blue-500 hover:bg-blue-600 text-white px-5 py-2 rounded-lg"
                        >
                          Save Note
                        </button>
                        <button
                          onClick={() => setIsAddingNote(false)}
                          className="bg-gray-400 hover:bg-gray-500 text-white px-5 py-2 rounded-lg"
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  )}

                  {/* Notes list */}
                  {notes.length === 0 ? (
                    <p>No notes yet.</p>
                  ) : (
                    <ul style={{ listStyle: "none", padding: 0 }}>
                      {notes.map((note) => (
                        <li
                          key={note.note_id}
                          style={{ border: "1px solid #ccc", padding: "0.5rem", marginBottom: "0.5rem" }}
                        >
                          <p>{note.note_text}</p>
                          <small>
                            {new Date(note.createdAt).toLocaleString()}
                          </small>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              )}

              {/* Files Tab */}
              {activeTab === "files" && (
                <div className="p-6 space-y-4">
                  <h3 className="text-lg font-semibold mb-4">Files & Documents</h3>

                  {can('insurers', 'write') && !isUploadingFile && (
                    <button
                      onClick={() => setIsUploadingFile(true)}
                      className="bg-green-500 hover:bg-green-600 text-white px-5 py-2 rounded-lg"
                    >
                      Create Document
                    </button>
                  )}

                  {isUploadingFile && (
                    <div className="space-y-4 bg-gray-50 p-6 rounded-lg shadow">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          File Type
                        </label>
                        <select
                          value={fileType}
                          onChange={(e) => setFileType(e.target.value)}
                          className="w-full border rounded-lg px-3 py-2"
                        >
                          <option value="">Select File Type</option>
                          <option value="Company Registration Documents">
                            Company Registration Documents
                          </option>
                          <option value="Identification Documents">
                            Identification Documents
                          </option>
                          <option value="Other">Other</option>
                        </select>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Description
                        </label>
                        <input
                          type="text"
                          value={description}
                          onChange={(e) => setDescription(e.target.value)}
                          className="w-full border rounded-lg px-3 py-2"
                          placeholder="Enter description"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Upload Document
                        </label>
                        <input
                          type="file"
                          onChange={(e) => setSelectedFile(e.target.files?.[0] || null)}
                          className="w-full"
                        />
                      </div>

                      <div className="flex gap-3 mt-4">
                        <button
                          onClick={handleFileUpload}
                          className="bg-blue-500 hover:bg-blue-600 text-white px-5 py-2 rounded-lg"
                        >
                          Upload
                        </button>
                        <button
                          onClick={() => setIsUploadingFile(false)}
                          className="bg-gray-400 hover:bg-gray-500 text-white px-5 py-2 rounded-lg"
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  )}

                  {underwriter.files && underwriter.files.length > 0 ? (
                    <div className="mt-6">
                      <h4 className="text-md font-semibold mb-2">Uploaded Files</h4>
                      <div className="overflow-x-auto">
                        <table className="w-full border border-gray-200 rounded-lg">
                          <thead className="bg-gray-100">
                            <tr key="header">
                              <th className="text-left p-2 border-b">File Type</th>
                              <th className="text-left p-2 border-b">Description</th>
                              <th className="text-left p-2 border-b">Uploaded</th>
                              <th className="text-left p-2 border-b">Actions</th>
                            </tr>
                          </thead>
                          <tbody>
                            {underwriter.files.map((file) => (
                              <tr key={file.file_id || file.document_id || `file-${file.file_type}-${file.description}`} className="border-b">
                                <td className="p-2">{file.file_type}</td>
                                <td className="p-2">{file.description}</td>
                                <td className="p-2 text-gray-500">
                                  {new Date(file.created_at).toLocaleDateString()}
                                </td>
                                <td className="p-2">
                                  <a
                                    href={`${API_URL}/${file.file_path}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-blue-600 hover:underline"
                                  >
                                    Download
                                  </a>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  ) : (
                    <p className="text-gray-500 mt-4">No files available.</p>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

              <EditUnderwriterModal
                isOpen={isEditModalOpen}
                onClose={() => setIsEditModalOpen(false)}
                onSave={fetchUnderwriterDetails}
                underwriterId={Number(underwriterId)}
                />

    </div>
  );
};

export default UnderwriterDetails;